import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAccess } from "../../../access/hooks/useAccess";
import { primeOperationalAccess, useOperationalAccess } from "../../../access/hooks/useOperationalAccess";
import { lockReasonText } from "../../../access/subscription";
import {
  filterDashboardBranches,
  filterDashboardStores,
  ludDashboardPermissions,
  resolveDashboardMode,
} from "../../../access/modules/lud-dashboard";
import { integrationLabel, normalizeIntegrationKey } from "../../../utils/integrations";
import { readCashierSelection, saveCashierSelection } from "../../../utils/cashierSelection";
import {
  listDashboardStoresAPI,
  getDashboardOverviewAPI,
  updateDashboardPlatformLinksAPI,
  uploadDashboardBannerAPI,
  removeDashboardBannerAPI,
  createDashboardRequestAPI,
  reviewDashboardRequestAPI,
  uploadDashboardRequestFileAPI,
  executeDashboardActionAPI,
  updateDashboardCompletionItemAPI,
  listDashboardNotificationsAPI,
  getDashboardRequestFileUrlAPI,
  uploadBranchLicenseAPI,
  removeBranchLicenseAPI,
  getBranchLicenseUrlAPI,
} from "../../../utils/api/ludDashboard";
import { FEATURE_NAMES, isFeatureEnabled } from "../../../utils/features";
import {
  uploadOrganizationDocumentAPI,
  deleteOrganizationDocumentAPI,
  getLudDocumentFileUrlAPI,
} from "../../../utils/api/ludDocuments";
import {
  getDashboardBillingStatsAPI,
  getSubscriptionSummaryAPI,
} from "../../../utils/api/billing";
import {
  SUBSCRIPTION_SUMMARY_BATCH_SIZE,
  getStoreSubscription,
  mergeDashboardBillingStats,
} from "../data/subscriptionSummary";

// Customer actions that activate or change something operational. They need an active subscription (a store whose subscription ended
// is locked like a new client); the other requests of a customer (details, hours, ...) are not activations and stay available.
const OPERATIONAL_REQUEST_ACTIONS = new Set([
  "updateApplication",
  "updateBranchApps",
  "updateBranchStatus",
  "updateBranchIntegrationStatus",
]);

const getFileExtension = (file = {}, fallbackName = "") => {
  const name = String(
    fallbackName || file.originalName || file.name || "",
  ).trim();
  const dotIndex = name.lastIndexOf(".");
  if (dotIndex > -1 && dotIndex < name.length - 1) {
    return name.slice(dotIndex + 1).toLowerCase();
  }

  const mimeExtensions = {
    "application/pdf": "pdf",
    "application/vnd.ms-excel": "xls",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
    "application/zip": "zip",
    "application/x-zip-compressed": "zip",
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/webp": "webp",
    "image/gif": "gif",
    "image/svg+xml": "svg",
  };

  return mimeExtensions[String(file.mimeType || "").toLowerCase()] || "";
};

const normalizeFileBaseName = (value = "file") =>
  String(value || "file")
    .trim()
    .replace(/\.[^/.]+$/, "")
    .replace(/[/:*?"<>|]+/g, "-")
    .replace(/\s+/g, "-");

const getDashboardRequestFileName = ({ request, file, originalName }) => {
  const extension = getFileExtension(file, originalName);
  const target = normalizeFileBaseName(
    request?.target || request?.applicationName || "",
  );
  const kind = file?.kind || "attachment";
  const kindNames = {
    statement: "statement",
    taxInvoice: "tax-invoice",
    contract: "contract",
    signedContract: "signed-contract",
    receipt: "receipt",
    menu: "menu",
    report: "report",
  };
  const kindName = kindNames[kind];

  if (!kindName) {
    return originalName || file?.name || file?.originalName || "file";
  }

  const baseName = target ? `${target}-${kindName}` : kindName;
  return extension ? `${baseName}.${extension}` : baseName;
};

const canPreviewFile = (file = {}) => {
  const mimeType = String(file.mimeType || "").toLowerCase();
  const name = String(file.name || file.originalName || "").toLowerCase();
  return (
    mimeType === "application/pdf" ||
    mimeType.startsWith("image/") ||
    /\.(pdf|png|jpe?g|gif|webp|svg)$/i.test(name)
  );
};

const openOrDownloadFile = async ({ file, url, fileName }) => {
  if (!url) return;

  const resolvedName = fileName || file?.originalName || file?.name || "file";

  // نفس منطق صفحة المستندات: ملفات PDF والصور تُفتح مباشرة،
  // وباقي الأنواع تُجلب كـ Blob ثم تُحمّل بالاسم المحدد والامتداد الصحيح.
  if (canPreviewFile({ ...file, name: resolvedName })) {
    window.open(url, "_blank", "noopener,noreferrer");
    return;
  }

  const downloadResponse = await fetch(url);

  if (!downloadResponse.ok) {
    throw new Error(`Failed to download file: ${downloadResponse.status}`);
  }

  const blob = await downloadResponse.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = objectUrl;
  link.download = resolvedName;

  document.body.appendChild(link);
  link.click();
  link.remove();

  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

const DAY_KEYS = [
  "saturday",
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
];
const DAY_LABELS = {
  saturday: ["السبت", "Saturday"],
  sunday: ["الأحد", "Sunday"],
  monday: ["الاثنين", "Monday"],
  tuesday: ["الثلاثاء", "Tuesday"],
  wednesday: ["الأربعاء", "Wednesday"],
  thursday: ["الخميس", "Thursday"],
  friday: ["الجمعة", "Friday"],
};
const COMPLETION_DEFINITIONS = [
  ["store", "بيانات المتجر", "Store Data"],
  ["account", "بيانات الحساب", "Account Data"],
  ["documents", "المستندات المطلوبة", "Required Documents"],
  ["menu", "بيانات قائمة الطعام", "Menu Data"],
  ["branches", "بيانات الفروع", "Branches Data"],
];
// Canonical integration key; unknown names are compared case/space-insensitively.
const appKey = (name) =>
  normalizeIntegrationKey(name) ||
  String(name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
const statusMap = (value) => {
  const s = String(value ?? "").toLowerCase();
  if (["active", "true", "فعال"].includes(s)) return "active";
  if (
    [
      "inprogress",
      "pending",
      "new",
      "startsubscription",
      "جاري العمل",
    ].includes(s)
  )
    return "inProgress";
  if (["warning"].includes(s)) return "warning";
  if (["closed"].includes(s)) return "inactive";
  if (["maintenance"].includes(s)) return "maintenance";
  return "inactive";
};
const branchStatusMap = (value, fallback) => {
  const status = String(value || "").trim();
  if (
    [
      "new",
      "active",
      "closed",
      "inactive",
      "maintenance",
      "inProgress",
      "temporarilyClosed",
    ].includes(status)
  )
    return status;
  const fallbackStatus = String(fallback ?? "").trim();
  if (
    [
      "new",
      "active",
      "closed",
      "inactive",
      "maintenance",
      "inProgress",
      "temporarilyClosed",
    ].includes(fallbackStatus)
  )
    return fallbackStatus;
  if (fallback === true || fallbackStatus === "true") return "active";
  if (fallback === false || fallbackStatus === "false") return "closed";
  return "inactive";
};
const dateOnly = (value) => (value ? String(value).slice(0, 10) : "");
const normalizeHours = (workTime = []) => {
  const days = workTime?.[0]?.days || {};
  return DAY_KEYS.map((key) => ({
    dayAr: DAY_LABELS[key][0],
    dayEn: DAY_LABELS[key][1],
    closed: !days[key]?.morning?.from && !days[key]?.evening?.from,
    from1: days[key]?.morning?.from || "",
    to1: days[key]?.morning?.to || "",
    from2: days[key]?.evening?.from || "",
    to2: days[key]?.evening?.to || "",
  }));
};
// Working hours in the shape the server stores and validates (Store branch workTime).
export const serializeHours = (hours = []) => ({
  name: "الدورية",
  days: Object.fromEntries(
    DAY_KEYS.map((key, index) => [
      key,
      {
        morning: {
          from: hours[index]?.closed ? "" : hours[index]?.from1 || "",
          to: hours[index]?.closed ? "" : hours[index]?.to1 || "",
        },
        evening: {
          from: hours[index]?.closed ? "" : hours[index]?.from2 || "",
          to: hours[index]?.closed ? "" : hours[index]?.to2 || "",
        },
      },
    ]),
  ),
});
export const normalizeStoreSummary = (store) => ({
  id: store.id,
  nameAr: store.businessName || store.name || store.id,
  nameEn:
    store.moreDetails?.businessNameEn ||
    store.businessName ||
    store.name ||
    store.id,
  city: store.city || store.branches?.[0]?.city || "",
  status: store.status || "inactive",
  branches: Number(store.branchNumber ?? store.branches?.length ?? 0),
});
const normalizeOverview = (data) => {
  const store = data.store || {};
  const rawBranches = store.branches || [];
  const integrationProviderKey = appKey(
    store.moreDetails?.integrationName || store.moreDetails?.posName || "",
  );
  const hasStoreIntegration = Boolean(
    store.moreDetails?.hasIntegration ||
    store.moreDetails?.integrationName ||
    store.moreDetails?.posName,
  );
  const branches = rawBranches.map((b) => {
    const branchId = String(b._id || b.id || b.name);
    const branchIntegrationStatus = statusMap(b.integrationStatus);
    const branchHasIntegrationAccount = (b.userInfo || []).some((user) => {
      const userAppKey = appKey(user.app || user.name || "");
      if (!userAppKey) return false;
      if (integrationProviderKey) return userAppKey === integrationProviderKey;
      return [
        "feedus",
        "foodics",
        "rewaa",
        "marn",
        "fodo",
        "omegapos",
      ].includes(userAppKey);
    });
    const derivedIntegrationStatus =
      branchIntegrationStatus === "active" || branchHasIntegrationAccount
        ? "active"
        : branchIntegrationStatus === "inProgress"
          ? "inProgress"
          : hasStoreIntegration && !b.integrationStatus
            ? "active"
            : "inactive";
    return {
      id: branchId,
      nameAr: b.name || "",
      nameEn: b.name || "",
      cityAr: b.city || "",
      cityEn: b.city || "",
      phone: b.phone || "",
      locationUrl: b.location || "",
      coordination: b.coordination || "",
      status: branchStatusMap(b.dashboardStatus, b.status),
      integrationStatus: statusMap(derivedIntegrationStatus),
      maintenance: b.maintenance || null,
      apps: (b.apps || []).map((a) => a.app || a.name).filter(Boolean),
      users: (b.userInfo || []).map((u) => ({
        id: String(u._id || u.id || u.email),
        app: u.app || "",
        email: u.email || "",
        password: u.password || u.pass || "",
        // The server withholds branch users' passwords; they ask for one at a time.
        hasSecret: Boolean(u.password || u.pass || u.passwordAvailable),
        status: statusMap(u.status || "active"),
      })),
      appLinks: (b.apps || []).map((a) => ({
        app: a.app || a.name || "",
        name: a.name || a.app || "",
        id: a.id || "",
        status: statusMap(a.status),
        statusRaw: String(a.status || ""),
      })),
      hours: normalizeHours(b.workTime),
      license: b.license?.name || b.license?.mimeType ? b.license : null,
      raw: b,
    };
  });
  const allApps = new Map();
  // `key` is the canonical app key sent back in requests; names are labels.
  (store.userInfo || []).forEach((a) =>
    allApps.set(appKey(a.app), {
      id: String(a._id || a.id || appKey(a.app)),
      key: normalizeIntegrationKey(a.app) || a.app || "",
      nameAr: integrationLabel(a.app, "ar"),
      nameEn: integrationLabel(a.app, "en"),
      status: statusMap(a.status),
      activeBranches: 0,
    }),
  );
  rawBranches.forEach((b) =>
    (b.apps || []).forEach((a) => {
      const name = a.app || a.name;
      const key = appKey(name);
      const cur = allApps.get(key) || {
        id: String(a._id || a.id || key),
        key: normalizeIntegrationKey(name) || name,
        nameAr: integrationLabel(name, "ar"),
        nameEn: integrationLabel(name, "en"),
        status: "inactive",
        activeBranches: 0,
      };
      if (statusMap(a.status) === "active") cur.activeBranches += 1;
      if (cur.status !== "active") cur.status = statusMap(a.status);
      allApps.set(key, cur);
    }),
  );
  const applications = [...allApps.values()];
  const platformLinks = (data.platformLinks || []).map((item) => ({
    app: item.app || "",
    url: item.url || "",
  }));
  const platformLinkMap = new Map(
    platformLinks.map((item) => [appKey(item.app), item.url || ""]),
  );
  const accounts = (store.userInfo || []).map((a) => ({
    id: String(a._id || a.id),
    app: a.app || "",
    accountId: a.id || "",
    email: a.email || "",
    secret: a.pass || a.password || "",
    hasSecret: Boolean(a.pass || a.password || a.passwordAvailable),
    platformUrl: platformLinkMap.get(appKey(a.app)) || a.platformUrl || "",
    status: statusMap(a.status),
    raw: a,
  }));
  const managers = (data.accountManagers || []).map((m) => ({
    id: String(m._id || m.id),
    app: m.app || "",
    name: m.name || "",
    email: m.email || "",
    phone: m.phone || "",
  }));
  const campaigns = (data.campaigns || []).flatMap((c) =>
  ["deliveryCamp", "paidCamp"].flatMap((field) =>
    (c[field] || [])
      .filter((value) => value.dashboardVisablility === true)
      .map((value, index) => ({
        id: `${c._id}-${field}-${index}`,
        app: c.app,
        field,
        titleAr: value.ar || value,
        titleEn: value.en || value,
        dashboardVisablility: true,
        value,
        raw: c,
      })),
  ),
);
  const requests = (data.requests || []).map((r) => ({
    id: String(r._id),
    number: r.requestNumber,
    type: r.type || "",
    target: r.target || "",
    details: r.details || "",
    status: r.status,
    createdBy: r.createdByRole || "",
    createdAt: r.createdAt,
    payload: { action: r.action, ...(r.payload || {}) },
    attachments: (r.files || []).map((f) => f.name),
    files: r.files || [],
    contractFile: r.files?.find((f) => f.kind === "contract")?.name || "",
    signedContractFile:
      r.files?.find((f) => f.kind === "signedContract")?.name || "",
    rejectionReason: r.reviewNote || "",
    raw: r,
  }));
  const completionRecords = new Map(
    (data.completionItems || []).map((i) => [i.itemId || i.id, i]),
  );
  const completionItems = COMPLETION_DEFINITIONS.map(
    ([id, labelAr, labelEn]) => ({
      id,
      labelAr,
      labelEn,
      completed: Boolean(completionRecords.get(id)?.completed),
      data: completionRecords.get(id)?.data || null,
    }),
  );
  const operations = (data.operations || []).map((operation) => ({
    ...operation,
    id: String(operation._id || operation.id || ""),
    appName:
      operation.appName ||
      operation.app ||
      operation.application ||
      operation.applicationName ||
      "",
    app:
      operation.appName ||
      operation.app ||
      operation.application ||
      operation.applicationName ||
      "",
    application:
      operation.appName ||
      operation.app ||
      operation.application ||
      operation.applicationName ||
      "",
    applicationName:
      operation.appName ||
      operation.app ||
      operation.application ||
      operation.applicationName ||
      "",
    createdAt: operation.createdAt || operation.date || null,
    updatedAt: operation.updatedAt || operation.date || null,
  }));
  const organizationDocuments = (
    data.documents?.organizationDocuments || []
  ).map((document) => ({
    id: String(document._id || document.key),
    key: document.key,
    titleAr: document.titleAr || "",
    titleEn: document.titleEn || "",
    essential: Boolean(document.essential),
    uploaded: Boolean(document.currentFile),
    file: document.currentFile || null,
    history: document.history || [],
    expiryDate: dateOnly(document.expiryDate),
  }));
  // Content of every active menu of the restaurant.
  const menus = Array.isArray(data.menus) ? data.menus : [];
  const menuSections = menus.flatMap((menu) => menu.sections || []);
  const menuItems = menuSections.flatMap((section) => section.items || []);
  const menuSummary = {
    available: menus.length > 0,
    menusCount: menus.length,
    sectionsCount: menuSections.length,
    itemsCount: menuItems.length,
    boxesCount: menuItems.filter((item) => item.isBox).length,
    updatedAt: menus.map((menu) => menu.updatedAt).filter(Boolean).sort().pop() || null,
  };
  const hasRequiredStoreData = Boolean(
    store.id &&
    (store.businessName || store.name) &&
    store.phone &&
    store.email &&
    store.details?.commercialNumber &&
    store.details?.taxNumber,
  );
  const onboardingDocumentFlags = [
    store.moreDetails?.commercialRegister,
    store.moreDetails?.taxCertificate,
    store.moreDetails?.menuPdf,
    store.moreDetails?.restaurantLogo,
    store.moreDetails?.ibanCertificate,
    store.moreDetails?.shopLicense,
    store.moreDetails?.nationalAddress,
  ];
  const onboardingDocumentsComplete =
    onboardingDocumentFlags.length > 0 &&
    onboardingDocumentFlags.every(Boolean);
  const ludDocumentsComplete =
    organizationDocuments.length > 0 &&
    organizationDocuments.every(
      // the tax certificate is optional, whatever an older record says
      (document) => !document.essential || document.key === "tax" || document.uploaded,
    );
  const requiredDocumentsComplete = Boolean(
    ludDocumentsComplete || onboardingDocumentsComplete,
  );
  const menuComplete = Boolean(
    (menuSummary.available &&
      menuSummary.sectionsCount > 0 &&
      menuSummary.itemsCount > 0) ||
    store.moreDetails?.ludMenu,
  );
  const branchesComplete =
    branches.length > 0 &&
    branches.every(
      (branch) =>
        branch.nameAr && branch.phone && branch.cityAr && branch.locationUrl,
    );
  const accountsComplete =
    accounts.length > 0 &&
    accounts.every(
      (account) => account.app && account.email && account.accountId,
    );
  const inferredCompletion = {
    store: hasRequiredStoreData,
    account: accountsComplete,
    documents: requiredDocumentsComplete,
    menu: menuComplete,
    branches: branchesComplete,
  };
  const resolvedCompletionItems = completionItems.map((item) => ({
    ...item,
    completed: Boolean(item.completed || inferredCompletion[item.id]),
  }));
  return {
    store,
    branches,
    applications,
    accounts,
    managers,
    campaigns,
    requests,
    platformLinks,
    completionItems: resolvedCompletionItems,
    banner: data.banner || null,
    pricing: data.pricing || {
      lBase: 400,
      lExtra: 50,
      uBase: 600,
      uExtra: 200,
      dBase: 1400,
      dExtra: 500,
      integration: 600,
      customItems: [],
    },
    serviceActions: data.serviceActions || [],
    operations,
    organizationDocuments,
    menuSummary,
    documents: data.documents || null,
    menus,
    summary: data.summary || {},
  };
};

export default function useDashboard({ initialStoreId, context } = {}) {
  const token = localStorage.getItem("auth-token");
  const { can, role: userRole } = useAccess();
  // Which dashboard the user starts in (rules: access/modules/lud-dashboard.js).
  const actualRole = resolveDashboardMode(can);
  const [role, setRoleState] = useState(actualRole);
  // Only admins may switch views (admin <-> customer preview); everyone else
  // stays in the dashboard their access gives them.
  const setRole = useCallback(
    (next) => {
      if (next === actualRole || (actualRole === "admin" && next === "customer")) {
        setRoleState(next);
      }
    },
    [actualRole],
  );
  const [page, setPage] = useState(actualRole === "cashier" ? "cashier" : "dashboard");
  const [stores, setStores] = useState([]);
  const userKey = context?.currentUserInfo?._id || context?.currentUserInfo?.id;
  const [selectedStoreId, setSelectedStoreId] = useState(initialStoreId || "");
  const [state, setState] = useState(() => normalizeOverview({}));
  const [allStoreData, setAllStoreData] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const [subscriptionState, setSubscriptionState] = useState({
    status: "idle",
    byStoreId: {},
    error: "",
  });
  const [billingStats, setBillingStats] = useState({
    status: "idle",
    data: null,
    error: "",
  });
  useEffect(() => {
    setRoleState(actualRole);
    setPage(actualRole === "cashier" ? "cashier" : "dashboard");
  }, [actualRole]);
  const loadStores = useCallback(async () => {
    if (!token) return;
    const res = await listDashboardStoresAPI(token, context.storeAccess );
    // Only restaurants the user is assigned to (NA-039); never the raw answer.
    const next = filterDashboardStores(res.stores, {
      role: userRole,
      between: context?.storeAccess,
    }).map(normalizeStoreSummary);
    setStores(next);
    const inScope = (id) => (next.some((item) => item.id === id) ? id : "");
    // A cashier reopens on the store they last used, but only if the user can
    // still reach it (the remembered id is never requested unchecked).
    const remembered =
      actualRole === "cashier" ? inScope(readCashierSelection(userKey).storeId) : "";
    setSelectedStoreId(
      (current) =>
        inScope(current) ||
        inScope(initialStoreId) ||
        remembered ||
        next[0]?.id ||
        "",
    );
    return next;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, initialStoreId, userRole, actualRole, userKey, JSON.stringify(context?.storeAccess || [])]);
  // Only the most recently requested store may write its overview into state,
  // so a slow answer for a store the user already left cannot overwrite it.
  const latestRefresh = useRef("");
  const refresh = useCallback(
    async (storeId = selectedStoreId) => {
      if (!token || !storeId) return;
      latestRefresh.current = storeId;
      setLoading(true);
      setError("");
      try {
        const overviewRes = await getDashboardOverviewAPI(token, storeId);
        if (latestRefresh.current !== storeId) return;
        const [res, notificationsRes] = await Promise.all([
          Promise.resolve(overviewRes),
          // With the switch on only the total is requested (limit=0); a backend that predates `limit` answers with the whole list,
          // whose length is the same number, so both paths show the same count.
          listDashboardNotificationsAPI(
            token,
            isFeatureEnabled(FEATURE_NAMES.DASHBOARD_ALERT_COUNT) ? { storeId, limit: 0 } : { storeId },
          ).catch(() => ({
            notifications: [],
          })),
        ]);
        if (latestRefresh.current !== storeId) return;
        const normalized = normalizeOverview(res.data || {});
        setState(normalized);
        setStores((current) =>
          current.map((item) =>
            item.id === storeId
              ? normalizeStoreSummary(normalized.store)
              : item,
          ),
        );
        setRevision((value) => value + 1);
        setNotifications(notificationsRes.notifications || []);
        setNotificationCount(
          Number.isFinite(notificationsRes.total)
            ? notificationsRes.total
            : (notificationsRes.notifications || []).length,
        );
      } catch (e) {
        if (latestRefresh.current === storeId) setError(e.message);
      } finally {
        if (latestRefresh.current === storeId) setLoading(false);
      }
    },
    [token, selectedStoreId],
  );
  useEffect(() => {
    if (actualRole === "cashier" && stores.some((item) => item.id === selectedStoreId))
      saveCashierSelection(userKey, { storeId: selectedStoreId });
  }, [actualRole, stores, selectedStoreId, userKey]);
  // Arriving again with a DIFFERENT store (right after the customer added one): select it as soon as the list reaches it, and leave none of the
  // previously selected store's data on screen while the new one loads.
  const appliedInitialStore = useRef(initialStoreId || "");
  useEffect(() => {
    if (!initialStoreId || initialStoreId === appliedInitialStore.current) return;
    if (!stores.some((item) => item.id === initialStoreId)) return;
    appliedInitialStore.current = initialStoreId;
    setState(normalizeOverview({}));
    setNotifications([]);
    setNotificationCount(0);
    setSelectedStoreId(initialStoreId);
  }, [initialStoreId, stores]);
  useEffect(() => {
    loadStores().catch((e) => {
      setError(e.message);
      setLoading(false);
    });
  }, [loadStores]);
  useEffect(() => {
    if (selectedStoreId) refresh(selectedStoreId);
  }, [selectedStoreId, refresh]);
  useEffect(() => {
    if (
      !token ||
      !stores.length ||
      actualRole !== "admin"
    )
      return;
    Promise.all(
      stores.map(async (s) => {
        try {
          const r = await getDashboardOverviewAPI(token, s.id);
          return { store: s, ...normalizeOverview(r.data || {}) };
        } catch {
          return null;
        }
      }),
    ).then((rows) => setAllStoreData(rows.filter(Boolean)));
  }, [token, stores, actualRole, revision]);
  // Billing subscription summaries and aggregated billing stats for every
  // listed restaurant, fetched once here and shared by all dashboard views
  // (never per component).
  const storeIdsKey = stores.map((s) => s.id).join("|");
  useEffect(() => {
    if (!token || !storeIdsKey) return undefined;
    let cancelled = false;
    const ids = storeIdsKey.split("|");
    const batches = [];
    for (let i = 0; i < ids.length; i += SUBSCRIPTION_SUMMARY_BATCH_SIZE)
      batches.push(ids.slice(i, i + SUBSCRIPTION_SUMMARY_BATCH_SIZE));
    setSubscriptionState((current) => ({
      ...current,
      status: "loading",
      error: "",
    }));
    Promise.all(batches.map((batch) => getSubscriptionSummaryAPI(token, batch)))
      .then((responses) => {
        if (cancelled) return;
        const byStoreId = {};
        responses.forEach((res) =>
          (res.data?.summaries || []).forEach((summary) => {
            byStoreId[summary.storeId] = summary;
          }),
        );
        setSubscriptionState({ status: "ready", byStoreId, error: "" });
        // The same answer feeds every page's operational lock (access/hooks/useOperationalAccess): one request, one rule.
        primeOperationalAccess(Object.values(byStoreId), String(userKey || ""));
      })
      .catch((e) => {
        if (!cancelled)
          setSubscriptionState({
            status: "error",
            byStoreId: {},
            error: e.message,
          });
      });
    // Aggregated stats only feed the admin overview boxes.
    if (actualRole !== "admin")
      return () => {
        cancelled = true;
      };
    setBillingStats((current) => ({ ...current, status: "loading", error: "" }));
    Promise.all(batches.map((batch) => getDashboardBillingStatsAPI(token, batch)))
      .then((responses) => {
        if (cancelled) return;
        setBillingStats({
          status: "ready",
          data: mergeDashboardBillingStats(responses.map((res) => res.data)),
          error: "",
        });
      })
      .catch((e) => {
        if (!cancelled)
          setBillingStats({ status: "error", data: null, error: e.message });
      });
    return () => {
      cancelled = true;
    };
  }, [token, storeIdsKey, actualRole, userKey]);
  const storesWithSubscription = useMemo(
    () =>
      stores.map((store) => ({
        ...store,
        package: subscriptionState.byStoreId[store.id]?.plan?.code || "",
      })),
    [stores, subscriptionState.byStoreId],
  );
  // A branch user only sees the branches assigned to them (NA-039).
  const scopedBranches = useMemo(
    () =>
      filterDashboardBranches(state.branches, {
        role: userRole,
        assignedBranch: context?.branchAccess,
      }),
    [state.branches, userRole, context?.branchAccess],
  );
  const selectedSubscription = useMemo(
    () => getStoreSubscription(subscriptionState, selectedStoreId),
    [subscriptionState, selectedStoreId],
  );
  // Operational access of the selected store (subscription) combined with what the role may raise. A `new` client raises no request at
  // all before activation; a store whose subscription is not active raises no activation request. The server refuses the same calls.
  const operational = useOperationalAccess(selectedStoreId, { passive: true });
  const requestAccess = operational.check(ludDashboardPermissions.requests.write, { restaurantId: selectedStoreId });
  const requestLock = requestAccess.locked ? { reason: requestAccess.reason } : null;
  const assertMayRaise = (action) => {
    if (role !== "customer" && role !== "cashier") return;
    const blocked = !can(ludDashboardPermissions.requests.write)
      ? requestAccess.visible
      : requestAccess.locked && (!action || OPERATIONAL_REQUEST_ACTIONS.has(action));
    if (!blocked) return;
    const error = new Error(lockReasonText(requestAccess.reason, context?.lang));
    error.code = "SUBSCRIPTION_REQUIRED";
    throw error;
  };
  const run = async (action, payload, meta = {}) => {
    assertMayRaise(action);
    const body = {
      storeId: selectedStoreId,
      type: meta.type || action,
      target: meta.target || null,
      details: meta.details || null,
      action,
      payload,
    };
    if (role === "customer" || role === "cashier")
      await createDashboardRequestAPI(token, body);
    else {
      // Direct execution is for LUD administrators only (the server enforces it
      // too); the accountant view never executes actions.
      if (!can(ludDashboardPermissions.actions.execute)) {
        throw new Error("You are not allowed to execute this action");
      }
      await executeDashboardActionAPI(token, body);
    }
    await refresh();
    return role === "customer" || role === "cashier" ? "requested" : "saved";
  };
  const createRequest = async ({
    type,
    target,
    details,
    payload = {},
    attachments = [],
    createdBy,
  }) => {
    const action = payload.action;
    assertMayRaise();
    const clean = { ...payload };
    delete clean.action;
    const res = await createDashboardRequestAPI(token, {
      storeId: selectedStoreId,
      type,
      target,
      details,
      action,
      payload: clean,
    });
    if (attachments?.[0] instanceof File)
      await uploadDashboardRequestFileAPI(
        token,
        res.request._id,
        "attachment",
        attachments[0],
      );
    await refresh();
    return res.request;
  };
  const reviewRequest = async (id, decision, note = "") => {
    await reviewDashboardRequestAPI(token, id, decision, note);
    await refresh();
  };
  const notifyAction = async (subject, referenceId, actorRole) =>
    run(
      "contactManagerRequest",
      { subject, referenceId, actorRole },
      { type: subject, target: referenceId, details: subject },
    );
  const completionPercent = useMemo(
    () =>
      state.completionItems.length
        ? Math.round(
            (state.completionItems.filter((i) => i.completed).length /
              state.completionItems.length) *
              100,
          )
        : 0,
    [state.completionItems],
  );
  const saveAccount = (account) =>
    account.id
      ? run(
          "updateAccount",
          {
            id: account.id,
            accountId: account.id,
            changes: {
              app: account.app,
              email: account.email,
              id: account.accountId,
              pass: account.secret,
              status: account.status,
            },
          },
          {
            type: "تعديل حساب مستخدم",
            target: account.app,
            details: `تحديث بيانات حساب ${account.app}`,
          },
        )
      : run(
          "addAccount",
          {
            account: {
              app: account.app,
              email: account.email,
              id: account.accountId,
              pass: account.secret,
              status: account.status || "active",
            },
          },
          {
            type: "إضافة حساب مستخدم",
            target: account.app,
            details: `إضافة حساب ${account.app}`,
          },
        );
  const savePlatformLinks = async (platformLinks) => {
    await updateDashboardPlatformLinksAPI(token, platformLinks);
    await refresh();
  };
  const saveManager = (manager) =>
    run(
      "addManager",
      { manager },
      {
        type: "إضافة مدير حساب",
        target: manager.name,
        details: `إضافة مدير حساب ${manager.name}`,
      },
    );
  const deleteAccount = (id) =>
    run(
      "deactivateAccount",
      { accountId: id },
      {
        type: "إلغاء تفعيل حساب مستخدم",
        target: id,
        details: "إلغاء تفعيل حساب مستخدم",
      },
    );
  const deleteManager = (id) =>
    run(
      "deleteManager",
      { managerId: id },
      { type: "حذف مدير حساب", target: id, details: "حذف مدير حساب" },
    );
  const addCampaign = (campaign) =>
    run(
      "addCampaign",
      { campaign },
      {
        type: "إضافة حملة",
        target: campaign.app,
        details: "إضافة أو تحديث حملة",
      },
    );
  const requestApplicationStatus = (application, status, branchIds = []) =>
    run(
      "updateApplication",
      {
        applicationId: application.id,
        applicationName: application.key || application.nameAr,
        status,
        branchIds,
      },
      {
        type: status === "active" ? "طلب تفعيل تطبيق" : "طلب إغلاق تطبيق",
        target: application.nameAr,
        details: `تحديث حالة ${application.nameAr}`,
      },
    );
  const addBranch = (branch) =>
    run(
      "addBranch",
      {
        branch: {
          name: branch.nameAr,
          city: branch.cityAr,
          phone: branch.phone,
          location: branch.locationUrl,
          coordination: branch.coordination,
          status: branch.status,
          apps: branch.apps || [],
          userInfo: branch.users || [],
          workTime: [serializeHours(branch.hours)],
        },
      },
      {
        type: "طلب إضافة فرع جديد",
        target: branch.nameAr,
        details: `${branch.cityAr} - ${branch.phone}`,
      },
    );
  const saveBranchHours = (branchId, hours) =>
    run(
      "updateBranchHours",
      { branchId, workTime: [serializeHours(hours)] },
      {
        type: "تعديل أوقات العمل",
        target: state.branches.find((b) => b.id === branchId)?.nameAr,
        details: "تعديل أوقات العمل اليومية",
      },
    );
  const updateBranch = (branchId, changes) =>
    run(
      "updateBranch",
      { branchId, changes },
      {
        type: "تعديل بيانات الفرع",
        target: state.branches.find((b) => b.id === branchId)?.nameAr,
        details: "تعديل بيانات الفرع",
      },
    );
  const updateBranchApps = (branchId, apps) =>
    run(
      "updateBranchApps",
      {
        branchId,
        apps: apps.map((app) =>
          typeof app === "string" ? { app, status: "active" } : app,
        ),
      },
      {
        type: "تعديل تطبيقات الفرع",
        target: state.branches.find((b) => b.id === branchId)?.nameAr,
        details: "تحديث التطبيقات المرتبطة",
      },
    );
  const updateBranchStatus = (branchId, status) =>
    run(
      "updateBranchStatus",
      { branchId, status },
      {
        type: "تغيير حالة الفرع",
        target: state.branches.find((b) => b.id === branchId)?.nameAr,
        details: `تغيير حالة الفرع إلى ${status}`,
      },
    );
  const updateBranchIntegrationStatus = (branchId, status) =>
    run(
      "updateBranchIntegrationStatus",
      { branchId, status },
      {
        type: "تغيير حالة التكامل",
        target: state.branches.find((b) => b.id === branchId)?.nameAr,
        details: `تغيير حالة التكامل إلى ${status}`,
      },
    );
  const addBranchUser = (branchId, user) =>
    run(
      "addBranchUser",
      { branchId, user },
      {
        type: "إضافة مستخدم للفرع",
        target: state.branches.find((b) => b.id === branchId)?.nameAr,
        details: "إضافة مستخدم للفرع",
      },
    );
  const deleteBranch = (branchId) =>
    run(
      "deleteBranch",
      { branchId },
      {
        type: "حذف فرع",
        target: state.branches.find((b) => b.id === branchId)?.nameAr,
        details: "طلب حذف الفرع",
      },
    );
  const uploadContractForCustomer = async (requestId, file) => {
    await uploadDashboardRequestFileAPI(token, requestId, "contract", file);
    await refresh();
  };
  const uploadSignedContract = async (requestId, file) => {
    await uploadDashboardRequestFileAPI(
      token,
      requestId,
      "signedContract",
      file,
    );
    await refresh();
  };
  const reviewSignedContract = (requestId, accepted, reason = "") =>
    reviewRequest(requestId, accepted ? "approved" : "rejected", reason);
  const completeItem = async (item, data) => {
    if (!item?.id) return;
    await updateDashboardCompletionItemAPI(
      token,
      selectedStoreId,
      item.id,
      data ?? item.data ?? null,
    );
    await refresh();
  };

  const saveOrganizationDocuments = async (
    filesByKey = {},
    completionItem = null,
  ) => {
    const entries = Object.entries(filesByKey || {}).filter(
      ([, file]) => typeof File !== "undefined" && file instanceof File,
    );
    for (const [documentKey, file] of entries) {
      await uploadOrganizationDocumentAPI(
        token,
        selectedStoreId,
        documentKey,
        file,
      );
    }
    if (completionItem) {
      await updateDashboardCompletionItemAPI(
        token,
        selectedStoreId,
        completionItem.id,
        completionItem.data || null,
      );
    }
    await refresh();
  };
  const deleteOrganizationDocument = async (documentKey) => {
    if (!documentKey) return;
    await deleteOrganizationDocumentAPI(token, selectedStoreId, documentKey);
    await refresh();
  };
  const openOrganizationDocument = async (document) => {
    const file = document?.file || document?.currentFile || document;
    const documentKey = document?.key || document?.id;
    if (!file || !documentKey) return;
    const response = await getLudDocumentFileUrlAPI(token, selectedStoreId, {
      kind: "organization",
      reference: documentKey,
    });
    await openOrDownloadFile({
      file: { ...file, mimeType: response?.mimeType || file.mimeType },
      url: response?.url || file.url,
      fileName:
        response?.originalName ||
        file.originalName ||
        file.name ||
        document?.titleAr ||
        "file",
    });
  };
  const openRequestFile = async (requestId, file) => {
    if (!requestId || !file) return;
    const request = state.requests.find((item) => item.id === requestId);
    const response = await getDashboardRequestFileUrlAPI(
      token,
      requestId,
      file._id || file.id,
    );
    const originalName =
      response.originalName || file.name || file.originalName;
    await openOrDownloadFile({
      file: { ...file, mimeType: response.mimeType || file.mimeType },
      url: response.url,
      fileName: getDashboardRequestFileName({ request, file, originalName }),
    });
  };
  const uploadRequestAttachment = async (requestId, kind, file) => {
    await uploadDashboardRequestFileAPI(
      token,
      requestId,
      kind || "attachment",
      file,
    );
    await refresh();
  };
  const uploadBanner = async (file) => {
    if (!(file instanceof File)) throw new Error("file is required");
    await uploadDashboardBannerAPI(token, selectedStoreId, file);
    await refresh();
  };
  const removeBanner = async () => {
    await removeDashboardBannerAPI(token, selectedStoreId);
    await refresh();
  };
  const uploadBranchLicense = async (branchId, file) => {
    if (!(file instanceof File)) throw new Error("file is required");
    await uploadBranchLicenseAPI(token, selectedStoreId, branchId, file);
    await refresh();
  };
  const removeBranchLicense = async (branchId) => {
    await removeBranchLicenseAPI(token, selectedStoreId, branchId);
    await refresh();
  };
  const getBranchLicenseUrl = async (branchId) => {
    const res = await getBranchLicenseUrlAPI(token, selectedStoreId, branchId);
    return res.url;
  };
  const updatePricing = (pricing) =>
    run(
      "updatePricing",
      { pricing },
      {
        type: "تعديل الأسعار والبنود",
        target: "لوحة التحكم",
        details: "تحديث الأسعار والبنود",
      },
    );
  return {
    role,
    setRole,
    page,
    setPage,
    stores: storesWithSubscription,
    subscriptionState,
    selectedSubscription,
    operational,
    requestLock,
    billingStats,
    selectedStoreId,
    setSelectedStoreId,
    allStoreData,
    loading,
    error,
    refresh,
    ...state,
    branches: scopedBranches,
    notifications,
    notificationCount,
    completionPercent,
    emailLog: [],
    notifyAction,
    saveAccount,
    savePlatformLinks,
    saveManager,
    deleteAccount,
    deleteManager,
    addCampaign,
    requestApplicationStatus,
    addBranch,
    saveBranchHours,
    updateBranch,
    updateBranchApps,
    updateBranchStatus,
    updateBranchIntegrationStatus,
    addBranchUser,
    deleteBranch,
    uploadBranchLicense,
    removeBranchLicense,
    getBranchLicenseUrl,
    createRequest,
    reviewRequest,
    uploadContractForCustomer,
    uploadSignedContract,
    reviewSignedContract,
    completeItem,
    saveOrganizationDocuments,
    deleteOrganizationDocument,
    openOrganizationDocument,
    openRequestFile,
    uploadRequestAttachment,
    uploadBanner,
    removeBanner,
    updatePricing,
  };
}
